import { HeaderTabsComponent } from "@swivl-mono/ui";
import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { readPersistedDomain, useDomain } from "../../Models/Domain/Domain.Context";
import { DOMAIN_MANIFESTS } from "../../Models/Domain/Domain.Manifests";
import { NavBar } from "../../Models/NavBar/NavBar.Model";
import { AdminUserStoriesEditor } from "../Admin/UserStories/Admin.UserStoriesEditor";
import { DomainPicker } from "./Components/DomainPicker";

const BotPromptsSettingView = lazy(() => import("../Settings/BotPrompts/BotPromptsSettingView"));
const AIConversationsView = lazy(() => import("../AIConversations/AIConversationsView"));
const TestingLayout = lazy(() => import("./Testing/TestingLayout"));

type AIStudioPage = "prompts" | "conversations" | "testing" | "evals" | "user-stories";

const ALL_TABS: { label: string; page: AIStudioPage }[] = [
  { label: "Prompts", page: "prompts" },
  { label: "Conversations", page: "conversations" },
  { label: "Testing", page: "testing" },
  // User Stories is always visible. The save endpoint writes source files via
  // fs — only meaningful against a checked-out repo, so on non-DEV servers the
  // page is read-only (banner + disabled save/delete/draft, server returns 503
  // on write attempts). Read still works everywhere a super-admin can hit
  // `/user-stories`.
  { label: "User Stories", page: "user-stories" }
];

/**
 * Renders the NavBar (title + domain picker + tabs) for the current Domain.
 * Tabs are filtered by the active Domain's manifest.
 */
function AIStudioHeader(): null {
  const { activeManifest, studioPath } = useDomain();

  useEffect(() => {
    const items = ALL_TABS.filter((tab) => activeManifest.aiStudioPages.includes(tab.page)).map((tab) => ({
      label: tab.label,
      to: studioPath(tab.page)
    }));
    // Right slot is replaced with the DomainPicker while on AI Studio pages
    // (overrides the default right-side widgets for the duration of the visit).
    NavBar.set(<NavBar.Component.Title title="AI Studio" />, <HeaderTabsComponent items={items} />, <DomainPicker />);
  }, [activeManifest, studioPath]);

  return null;
}

/**
 * Inner routes — rendered inside <DomainProvider /> so every view can use
 * useDomain() to resolve scoped paths.
 */
function DomainScopedRoutes(): React.ReactElement {
  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden">
      <AIStudioHeader />
      <div className="relative min-h-0 flex-1">
        <Suspense fallback={<div className="flex h-full items-center justify-center text-grey-400">Loading...</div>}>
          <Routes>
            <Route path="prompts" element={<BotPromptsSettingView />} />
            <Route path="prompts/:slug" element={<BotPromptsSettingView />} />
            <Route path="conversations" element={<AIConversationsView />} />
            <Route path="conversations/:conversationId" element={<AIConversationsView />} />
            <Route path="testing/*" element={<TestingLayout />} />
            <Route path="evals" element={<EvalsToTesting />} />
            <Route path="evals/:runId" element={<EvalsToTesting />} />
            {/* User Stories — visible in every env. The view checks the
                list-endpoint's `readOnly` flag and renders read-only on
                non-DEV servers (saves are server-guarded too). */}
            <Route path="user-stories" element={<AdminUserStoriesEditor />} />
            <Route path="user-stories/:storyId" element={<AdminUserStoriesEditor />} />
            <Route index element={<DefaultPageRedirect />} />
            <Route path="*" element={<DefaultPageRedirect />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

/**
 * When the domain is in the URL but no page is specified, redirect to the
 * first AI Studio page the domain declares in its manifest.
 */
function DefaultPageRedirect(): React.ReactElement {
  const { activeManifest, studioPath } = useDomain();
  const page = activeManifest.aiStudioPages[0] ?? "prompts";
  return <Navigate to={studioPath(page)} replace />;
}

/**
 * Legacy eval routes (/ai-studio/<d>/evals[/runId]) → redirect to Testing →
 * Evaluations subview. Kept for backward compat with prior bookmarks.
 */
function EvalsToTesting(): React.ReactElement {
  const { studioPath } = useDomain();
  const { runId } = useParams<{ runId?: string }>();
  return <Navigate to={studioPath("testing", "evaluations", runId)} replace />;
}

/**
 * Handles URLs that hit /ai-studio/* without a valid :domain segment.
 * Includes legacy top-level paths like /ai-studio/prompts (no domain).
 * Redirects to /ai-studio/<persisted-or-default>/<same-subpath>.
 */
function LegacyNoDomainRedirect(): React.ReactElement {
  const location = useLocation();
  const domain = readPersistedDomain();
  // Strip the /ai-studio prefix; whatever's left is the sub-path (may be empty).
  const rest = location.pathname.replace(/^\/ai-studio\/?/, "");
  // If the first segment happens to be a valid domain, don't interfere — this component should not have been rendered.
  const firstSegment = rest.split("/")[0];
  if (DOMAIN_MANIFESTS.some((m) => m.id === firstSegment)) {
    return <Navigate to={location.pathname + location.search} replace />;
  }
  const target = rest ? `/ai-studio/${domain}/${rest}` : `/ai-studio/${domain}/prompts`;
  return <Navigate to={target + location.search} replace />;
}

export default function AIStudioView(): React.ReactElement {
  return (
    <Routes>
      {/* Domain-scoped primary routes. DomainProvider lives higher (in NavBarLayout)
          so it covers the global nav bar's DomainPicker too. */}
      <Route path=":domain/*" element={<DomainScopedRoutes />} />
      {/* Bare /ai-studio and legacy no-domain paths redirect to the persisted/default domain */}
      <Route index element={<LegacyNoDomainRedirect />} />
      <Route path="*" element={<LegacyNoDomainRedirect />} />
    </Routes>
  );
}
